import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Clock, RefreshCw, LogOut, ShieldCheck, Home, Phone, Car, Bike, Mail, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { parseVehicles } from "@/lib/vehicles";

export default function PendingApproval() {
    const { user, refresh, logout } = useAuth();
    const navigate = useNavigate();
    const [checking, setChecking] = useState(false);

    const handleCheckStatus = async () => {
        setChecking(true);
        try {
            await refresh();
            // Let the state update take effect
            setTimeout(() => {
                if (user?.approval_status === "approved") {
                    toast.success("Congratulations! Your resident profile has been approved.");
                    navigate("/dashboard", { replace: true });
                } else if (user?.approval_status === "rejected") {
                    toast.error(user?.rejection_reason || "Your profile was rejected. Please contact the society office.");
                } else {
                    toast.info("Your application is still under review by the society administrator.");
                }
                setChecking(false);
            }, 400);
        } catch {
            setChecking(false);
        }
    };

    const vehicles = parseVehicles(user?.vehicles);

    return (
        <div className="min-h-screen bg-nest-base flex flex-col justify-between p-6 lg:p-12" data-testid="pending-approval-page">
            {/* Top brand */}
            <div className="flex items-center justify-between max-w-2xl mx-auto w-full">
                <div className="flex items-center gap-2.5">
                    <div className="h-8 w-8 rounded-md bg-nest-ink text-white grid place-items-center font-display font-semibold">N</div>
                    <span className="font-display text-lg tracking-tight">NeighborNest</span>
                </div>
                <button
                    onClick={logout}
                    className="flex items-center gap-2 text-xs font-semibold text-nest-ink3 hover:text-nest-ink px-3 py-1.5 rounded-lg border border-nest-line hover:bg-white transition"
                    data-testid="pending-logout-btn"
                >
                    <LogOut size={13} /> Sign out
                </button>
            </div>

            {/* Central Card */}
            <div className="max-w-xl mx-auto w-full my-auto py-8">
                <div className="nest-card p-8 sm:p-10 text-center shadow-xl border border-nest-line/80 relative overflow-hidden">
                    <div className="absolute top-0 inset-x-0 h-1.5 bg-gradient-to-r from-amber-400 via-amber-500 to-amber-600"></div>

                    <div className="h-16 w-16 rounded-2xl bg-amber-50 border border-amber-200 text-amber-600 mx-auto grid place-items-center mb-5 shadow-xs">
                        <Clock size={32} className="animate-pulse" />
                    </div>

                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider bg-amber-100/70 text-amber-800 border border-amber-300">
                        <span className="h-2 w-2 rounded-full bg-amber-500"></span> Verification Pending
                    </span>

                    <h1 className="font-display text-3xl sm:text-4xl mt-3 tracking-tight text-nest-ink">
                        Profile Under Review
                    </h1>

                    <p className="mt-3 text-nest-ink2 text-sm leading-relaxed max-w-md mx-auto">
                        Hi <strong>{user?.name || "Resident"}</strong>, your resident profile as <strong>{user?.occupancy === "tenant" ? "a Tenant" : "an Owner"}</strong> for flat <strong>{user?.tower}-{user?.flat_no}</strong> has been submitted to the society office. Once the admin verifies your details, your full access will be enabled.
                    </p>

                    {/* Summary of submitted details */}
                    <div className="mt-6 text-left bg-nest-alt/50 rounded-xl p-5 border border-nest-line/80 space-y-2.5 text-xs text-nest-ink2">
                        <div className="flex items-center justify-between pb-2 border-b border-nest-line/60">
                            <span className="text-nest-ink3 font-medium">Application Summary</span>
                            <span className="font-mono text-[11px] text-nest-ink">{user?.email}</span>
                        </div>

                        <div className="flex items-center justify-between">
                            <span className="flex items-center gap-1.5 text-nest-ink3"><Home size={14} className="text-nest-clay" /> Tower & Flat:</span>
                            <span className="font-semibold text-nest-ink">{user?.tower}-{user?.flat_no}</span>
                        </div>

                        <div className="flex items-center justify-between">
                            <span className="flex items-center gap-1.5 text-nest-ink3"><ShieldCheck size={14} className="text-nest-clay" /> Flat Occupancy:</span>
                            <span className={`font-semibold uppercase tracking-wider px-2 py-0.5 rounded text-[10px] ${
                                user?.occupancy === "tenant" ? "bg-blue-100 text-blue-800 border border-blue-200" : "bg-emerald-100 text-emerald-800 border border-emerald-200"
                            }`}>
                                {user?.occupancy === "tenant" ? "Tenant" : "Flat Owner"}
                            </span>
                        </div>

                        {user?.phone && (
                            <div className="flex items-center justify-between">
                                <span className="flex items-center gap-1.5 text-nest-ink3"><Phone size={14} className="text-nest-clay" /> Phone:</span>
                                <span className="font-medium text-nest-ink">{user?.phone}</span>
                            </div>
                        )}

                        {vehicles.length > 0 && (
                            <div className="flex items-center justify-between pt-1">
                                <span className="flex items-center gap-1.5 text-nest-ink3"><Car size={14} className="text-nest-clay" /> Vehicles ({vehicles.length}):</span>
                                <div className="flex gap-1.5 flex-wrap justify-end">
                                    {vehicles.map((v, i) => (
                                        <span key={i} className="font-mono text-[11px] bg-white px-2 py-0.5 rounded border border-nest-line">
                                            {v.number}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Action buttons */}
                    <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                        <button
                            onClick={handleCheckStatus}
                            disabled={checking}
                            className="w-full sm:w-auto nest-btn-primary flex items-center justify-center gap-2 px-6 py-3"
                            data-testid="check-status-btn"
                        >
                            <RefreshCw size={15} className={checking ? "animate-spin" : ""} />
                            {checking ? "Checking..." : "Check Approval Status"}
                        </button>
                    </div>

                    <p className="mt-6 text-[11px] text-nest-ink3">
                        Need urgent approval? Please contact your society manager or secretary with your flat number.
                    </p>
                </div>
            </div>

            {/* Footer */}
            <div className="text-center text-xs text-nest-ink3">
                NeighborNest Society Operating System · Secure Gated Access
            </div>
        </div>
    );
}
